import { HFlow } from 'bold-ui'
import { purple } from 'bold-ui/lib/styles/colors'
import { parseISO } from 'date-fns'
import { useAcompanhamentoPreNatalCardQuery, useDataUltimaConsultaOdontoGestanteQuery } from 'graphql/hooks.generated'
import { useMemo } from 'react'
import { STATUS_DUM_ATENDIMENTO_ANTERIOR } from 'view/atendimentos/detail/components/modals/medicoes/model-medicoes'
import { MedicoesPreNatalAtendimentoModel } from 'view/atendimentos/detail/components/modals/types/model-historicoPreNatal'

import { RiscoGravidezTag } from '../../../pre-natal/components/RiscoGravidezTag'
import { ProntuarioCard } from '../../ProntuarioCard'
import { usePeriodoGestacional } from '../hooks/usePeriodoGestacional'
import { IdadeGestacionalAcompanhamentoPreNatal } from '../model-acompanhamentoPreNatal'
import { convertAcompanhamentoPreNatalCardQueryToPreNatalCardContentModel } from '../util/util-acompanhamentoPreNatal'
import { ImprimirPreNatalButton } from './ImprimirPreNatalButton'
import { PreNatalCard } from './PreNatalCard'

interface PreNatalSectionProps extends Omit<MedicoesPreNatalAtendimentoModel, 'dataAtendimento'> {
  atendimentoId: ID
  prontuarioId: ID
  cidadaoId: ID
  isAltoRiscoAtendimentoAtual?: boolean
  readOnly?: boolean
  idadeGestacional?: IdadeGestacionalAcompanhamentoPreNatal
  dataAtendimento: Instant
  viaFolhaRosto?: boolean
}

const preNatalSectionHeaderContent = (
  isDisabled: boolean,
  atendimentoId: ID,
  prontuarioId: ID,
  dataAtendimento: Instant
) => (
  <ImprimirPreNatalButton
    disabled={isDisabled}
    atendimentoId={atendimentoId}
    prontuarioId={prontuarioId}
    dataAtendimento={dataAtendimento}
    tooltipMessage={isDisabled && 'Não é possível imprimir pré-natal sem a DUM preenchida.'}
  />
)

const preNatalSectionHeaderTitle = (isAltoRisco: boolean) => (
  <HFlow hSpacing={0.75} alignItems='center'>
    Pré-natal
    <RiscoGravidezTag isAltoRisco={isAltoRisco} dataTestId='PreNatalCard.tagRisco' />
  </HFlow>
)

export function PreNatalSection({
  prontuarioId,
  cidadaoId,
  atendimentoId,
  isAltoRiscoAtendimentoAtual,
  preNatalAtendimentoAtual,
  dumAtendimentoAtual,
  resultadosExamesAtendimentoAtual,
  dataAtendimento,
  readOnly = false,
  idadeGestacional,
  viaFolhaRosto,
}: PreNatalSectionProps) {
  const { data, loading: loadingAcompanhamentoPreNatal } = useAcompanhamentoPreNatalCardQuery({
    variables: {
      input: prontuarioId,
    },
  })

  const dumAtendimentoAtualParsed = dumAtendimentoAtual && parseISO(dumAtendimentoAtual)

  const cardContent = useMemo(
    () =>
      convertAcompanhamentoPreNatalCardQueryToPreNatalCardContentModel(
        data.atendimentosProfUltimaGestacao,
        isAltoRiscoAtendimentoAtual,
        preNatalAtendimentoAtual,
        dumAtendimentoAtualParsed
      ),
    [data, isAltoRiscoAtendimentoAtual, preNatalAtendimentoAtual, dumAtendimentoAtualParsed]
  )

  const { dumStatus, isAltoRisco } = cardContent

  const periodoGestacional = usePeriodoGestacional({
    prontuarioId,
    dum: dumStatus.dumPersistida,
    dumAtendimentoAtual: dumAtendimentoAtualParsed,
    dataAtendimento,
    resultadosExamesAtendimentoAtual,
    fonteIgAtendimentoAtual: preNatalAtendimentoAtual?.fonteIdadeGestacional,
  })

  const { data: dataOdonto, loading: loadingDataOdonto } = useDataUltimaConsultaOdontoGestanteQuery({
    variables: {
      input: { prontuarioId, cidadaoId, dataInicioGestacao: periodoGestacional.dataInicioGestacao },
    },
    skip: loadingAcompanhamentoPreNatal || periodoGestacional.loading || !periodoGestacional.dataInicioGestacao,
  })

  const loadingPreNatal = loadingAcompanhamentoPreNatal || loadingDataOdonto || periodoGestacional.loading

  return (
    <ProntuarioCard
      title={preNatalSectionHeaderTitle(isAltoRisco)}
      titleBackgroundColor={purple.c90}
      content={preNatalSectionHeaderContent(
        !STATUS_DUM_ATENDIMENTO_ANTERIOR.has(dumStatus.origemDum),
        atendimentoId,
        prontuarioId,
        dataAtendimento
      )}
    >
      <PreNatalCard
        content={cardContent}
        dataUltimaConsultaOdontoGestante={dataOdonto?.dataUltimaConsultaOdontoGestante}
        loading={loadingPreNatal}
        viaFolhaRosto={viaFolhaRosto}
        readOnly={readOnly}
        periodoGestacional={{
          ...periodoGestacional,
          // TODO (eldorado #26937) centralizar fonte da idade gestacional
          idadeGestacional: idadeGestacional ?? periodoGestacional.idadeGestacional,
        }}
      />
    </ProntuarioCard>
  )
}
